/* eslint-disable react-hooks/preserve-manual-memoization --
   This editor wires Quill up imperatively: ref callbacks attach DOM/window listeners tied to the
   Quill instance's lifecycle (kept StrictMode-safe by re-running on mount/unmount), and refs mirror
   selection state for those listeners. React Compiler cannot preserve this manual memoization and
   skips optimizing the component, so the `useCallback`/`useMemo` below are load-bearing in every
   environment — jest and Cypress run no compiler, and the prod build's compiler skips this
   component. Dropping them reintroduces Quill's listener-churn / duplicate-binding bug. */
import {Show, Text, VStack} from 'platform/foundation';
import {AlignStyle} from 'quill/formats/align';
import type Toolbar from 'quill/modules/toolbar';

import {
  ComponentProps,
  ForwardedRef,
  MouseEvent,
  PointerEvent as ReactPointerEvent,
  forwardRef,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import ReactQuill, {Quill} from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

import {isNil} from 'ramda';
import {isNotNil} from 'ramda-adjunct';

import {suffixTestId} from 'shared/web';

import {HelperText} from '../HelperText/HelperText';
import {Label} from '../Label/Label';
import {useTranslationContext} from '../TranslationProvider/TranslationContext';
import {
  EmojiButton,
  EmojiCategoryButton,
  EmojiCategoryTabs,
  EmojiGrid,
  EmojiPanel,
  EmojiPickerWrapper,
} from './components/EmojiPicker';
import {FieldWrapper} from './components/FieldWrapper';
import {FooterBar, FooterCounter} from './components/FooterBar';
import {
  ImageMoveIndicator,
  ImageResizeCorner,
  ImageResizeHandle,
  ImageResizeOverlay,
} from './components/ImageResizeOverlay';
import {StyledQuillWrapper} from './components/QuillWrapper';
import {useRichTextEditor} from './hooks/useRichTextEditor';
import {RichTextProps, ToolbarOption} from './types';
import {buildCounterLabel} from './utils/buildCounterLabel';

// Output text alignment as an inline `text-align` style instead of Quill's default `ql-align-*`
// class, so aligned content stays centered/right where the class stylesheet is absent — e.g. an
// HTML signature rendered inside an outgoing email. Registered once, globally.
// The guard keeps the barrel importable from jest suites that don't mock `react-quill-new` (where
// `Quill` is undefined at module load); in the browser and in specs that mock Quill it runs as before.
if (isNotNil(Quill)) {
  Quill.register(AlignStyle, true);
}

const EMOJI_DATA = [
  {key: 'smileys', emojis: ['😀', '😄', '😉', '😊', '😍', '🤝']},
  {key: 'nature', emojis: ['🌞', '🌿', '🌲', '🌷', '🍀', '🔥']},
  {key: 'transport', emojis: ['🚗', '🚙', '🛻', '🏎️', '🛞', '⛽']},
  {key: 'symbols', emojis: ['⭐', '✅', '⚠️', '💡', '📌', '🛠️']},
] as const;

const ALL_TOOLBAR_OPTIONS: ToolbarOption[] = [
  'bold',
  'italic',
  'underline',
  'strike',
  'header',
  'blockquote',
  'listOrdered',
  'listBullet',
  'align',
  'clean',
  'emoji',
  'image',
];

// Maps each toolbar option to its underlying Quill format name.
// Options without a Quill format (clean, emoji) are absent.
const TOOLBAR_OPTION_TO_FORMAT: Partial<Record<ToolbarOption, string>> = {
  bold: 'bold',
  italic: 'italic',
  underline: 'underline',
  strike: 'strike',
  header: 'header',
  blockquote: 'blockquote',
  listOrdered: 'list',
  listBullet: 'list',
  align: 'align',
  // Whitelisting the `image` format keeps base64 images that the user inserts,
  // pastes, or drops instead of Quill stripping them from the value.
  image: 'image',
};

// The four draggable corners of the resize overlay.
const IMAGE_RESIZE_CORNERS: ImageResizeCorner[] = [
  'topLeft',
  'topRight',
  'bottomLeft',
  'bottomRight',
];

// Smallest width (px) a drag can shrink an image to.
const MIN_IMAGE_WIDTH = 24;

// Newly uploaded images wider than this are capped to it (height cleared, so the aspect ratio is
// kept) so a large image does not fill the editor. Smaller images keep their natural size.
const DEFAULT_IMAGE_WIDTH = 200;

// Uploads larger than this (longest side, px) are downscaled before being embedded as base64.
// The value is serialized on every keystroke, so a multi-megabyte data URL would make typing
// laggy; downscaling keeps it small. Images already within the limit are embedded unchanged.
const MAX_UPLOAD_IMAGE_DIMENSION = 1024;
const IMAGE_ENCODE_QUALITY = 0.82;

// Pointer travel (px) before pressing an image turns into a move drag rather than a click.
const IMAGE_MOVE_THRESHOLD = 4;

type ImageOverlayRect = {top: number; left: number; width: number; height: number};

// Position/size of the selection overlay, expressed relative to the (position: relative) editor
// wrapper so it can be absolutely placed over the image regardless of scroll.
function measureImageOverlay(image: HTMLImageElement, wrapper: HTMLElement): ImageOverlayRect {
  const wrapperRect = wrapper.getBoundingClientRect();
  const imageRect = image.getBoundingClientRect();
  return {
    top: imageRect.top - wrapperRect.top,
    left: imageRect.left - wrapperRect.left,
    width: imageRect.width,
    height: imageRect.height,
  };
}

// The overlay position changes every scroll/drag frame, so it is applied imperatively (a `style`
// prop would either be forbidden on the styled component or churn styled-components classes).
function applyOverlayRect(node: HTMLElement, rect: ImageOverlayRect) {
  node.style.top = `${rect.top}px`;
  node.style.left = `${rect.left}px`;
  node.style.width = `${rect.width}px`;
  node.style.height = `${rect.height}px`;
}

// Maps a viewport point to a document index using the caret at that point, so a dropped image
// lands where the cursor is (mirrors how Quill's uploader positions dropped files).
function getDropIndex(editor: QuillEditor, clientX: number, clientY: number): number | null {
  const caretRange = document.caretRangeFromPoint(clientX, clientY);
  if (isNil(caretRange)) {
    return null;
  }
  const blot = Quill.find(caretRange.startContainer, true);
  if (isNil(blot) || blot instanceof Quill) {
    return null;
  }
  return editor.getIndex(blot) + caretRange.startOffset;
}

// Position (relative to the wrapper) of the caret under the cursor — used to draw the insertion
// bar shown while an image is being moved.
function measureCaretIndicator(
  wrapper: HTMLElement,
  clientX: number,
  clientY: number
): ImageOverlayRect | null {
  const caretRange = document.caretRangeFromPoint(clientX, clientY);
  if (isNil(caretRange)) {
    return null;
  }
  const caretRect = caretRange.getBoundingClientRect();
  const wrapperRect = wrapper.getBoundingClientRect();
  return {
    top: caretRect.top - wrapperRect.top,
    left: caretRect.left - wrapperRect.left,
    width: caretRect.width,
    height: caretRect.height,
  };
}

function applyIndicatorRect(node: HTMLElement, rect: ImageOverlayRect) {
  node.style.top = `${rect.top}px`;
  node.style.left = `${rect.left}px`;
  node.style.height = `${rect.height}px`;
}

// Downscales an oversized uploaded image so the embedded base64 stays small (a multi-megabyte
// data URL in the value makes every keystroke re-serialize a huge string → typing lag). Returns
// null when the image is already within the limit, so the original data URL is embedded as-is
// (no re-encode, no quality loss). PNG sources keep transparency; everything else re-encodes to
// JPEG, which is much smaller for photos.
function downscaleImageDataUrl(image: HTMLImageElement, mimeType: string): string | null {
  const longestSide = Math.max(image.naturalWidth, image.naturalHeight);
  if (longestSide === 0 || longestSide <= MAX_UPLOAD_IMAGE_DIMENSION) {
    return null;
  }
  const scale = MAX_UPLOAD_IMAGE_DIMENSION / longestSide;
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(image.naturalWidth * scale);
  canvas.height = Math.round(image.naturalHeight * scale);
  const context = canvas.getContext('2d');
  if (isNil(context)) {
    return null;
  }
  context.drawImage(image, 0, 0, canvas.width, canvas.height);
  const outputType = mimeType === 'image/png' ? 'image/png' : 'image/jpeg';
  return canvas.toDataURL(outputType, IMAGE_ENCODE_QUALITY);
}

type QuillEditor = ReturnType<ReactQuill['getEditor']>;
type ToolbarModule = Toolbar & {
  __richTextSkipsNullRange?: boolean;
};

function patchToolbarNullRangeUpdate(editor: QuillEditor) {
  const toolbar = editor.getModule('toolbar') as ToolbarModule | null;

  if (!toolbar || toolbar.__richTextSkipsNullRange) {
    return;
  }

  const updateToolbar = toolbar.update.bind(toolbar);

  toolbar.update = (range) => {
    if (!range) {
      return;
    }

    updateToolbar(range);
  };
  toolbar.__richTextSkipsNullRange = true;
}

function handleToolbarMouseDown(event: MouseEvent<HTMLDivElement>) {
  const target = event.target;
  if (!(target instanceof HTMLElement)) {
    return;
  }

  const button = target.closest('button');
  if (button && event.currentTarget.contains(button)) {
    event.preventDefault();
  }
}

export const RichText = forwardRef(
  (props: RichTextProps, userRef: ForwardedRef<HTMLDivElement>) => {
    const t = useTranslationContext();
    const quillRef = useRef<ReactQuill>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const imageClickCleanupRef = useRef<(() => void) | null>(null);
    const resizeDragCleanupRef = useRef<(() => void) | null>(null);
    const selectedImageRef = useRef<HTMLImageElement | null>(null);
    const dragImageRef = useRef<HTMLImageElement | null>(null);
    const moveDragRef = useRef<{startX: number; startY: number; isMoving: boolean} | null>(null);
    const moveIndicatorRef = useRef<HTMLDivElement | null>(null);
    const moveIndicatorRectRef = useRef<ImageOverlayRect | null>(null);
    const suppressClickRef = useRef(false);
    const overlayRef = useRef<HTMLDivElement | null>(null);
    const overlayRectRef = useRef<ImageOverlayRect | null>(null);
    const resizeDragRef = useRef<{
      startX: number;
      startWidth: number;
      directionX: number;
      currentWidth: number;
    } | null>(null);

    const {
      id,
      helperId,
      toolbarId,
      isFocused,
      currentLength,
      isOverLimit,
      isNearLimit,
      handleFocus,
      handleBlur,
      onChange,
    } = useRichTextEditor(props, userRef);

    const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false);
    const [activeEmojiCategory, setActiveEmojiCategory] = useState(0);
    const [selectionPathKey, setSelectionPathKey] = useState('richText.formats.paragraph');
    const [selectedImage, setSelectedImage] = useState<HTMLImageElement | null>(null);
    const [isMovingImage, setIsMovingImage] = useState(false);

    // The selection state drives the overlay render; `selectedImageRef` mirrors it so the
    // imperatively-attached scroll/resize/drag listeners read the current selection without being
    // re-attached. The mirror is written alongside every `setSelectedImage` call in the event
    // handlers below (never during render), keeping it in sync while satisfying react-hooks/refs.

    // Applies the freshly measured overlay position to the DOM node (if it is mounted) and caches
    // it so the overlay's mount-time ref callback can restore it.
    const positionOverlay = (rect: ImageOverlayRect) => {
      overlayRectRef.current = rect;
      if (isNotNil(overlayRef.current)) {
        applyOverlayRect(overlayRef.current, rect);
      }
    };

    const handleOverlayRef = useCallback((node: HTMLDivElement | null) => {
      overlayRef.current = node;
      if (isNotNil(node) && isNotNil(overlayRectRef.current)) {
        applyOverlayRect(node, overlayRectRef.current);
      }
    }, []);

    const handleMoveIndicatorRef = useCallback((node: HTMLDivElement | null) => {
      moveIndicatorRef.current = node;
      if (isNotNil(node) && isNotNil(moveIndicatorRectRef.current)) {
        applyIndicatorRect(node, moveIndicatorRectRef.current);
      }
    }, []);

    const enabledOptions = props.toolbarOptions ?? ALL_TOOLBAR_OPTIONS;
    const enabledOptionSet = new Set(enabledOptions);

    // Callers commonly pass a fresh array literal each render. Deriving Quill's `formats`
    // prop from a content-based string key keeps its identity stable — without this, Quill
    // re-instantiates every render and stacks duplicate native click listeners on toolbar
    // buttons, so clicks fire N times and cancel out for even N (bold appears stuck, etc.).
    const quillFormatsKey = enabledOptions
      .map((option) => TOOLBAR_OPTION_TO_FORMAT[option])
      .filter((format): format is string => isNotNil(format))
      .join('|');

    const quillFormats = useMemo(
      () => (quillFormatsKey ? Array.from(new Set(quillFormatsKey.split('|'))) : []),
      [quillFormatsKey]
    );

    const emojiCategories = useMemo(
      () =>
        EMOJI_DATA.map((category) => ({
          label: t(`richText.emojiCategories.${category.key}`),
          emojis: category.emojis,
        })),
      [t]
    );

    const quillValue = props.value ?? '';

    const handleChange: ComponentProps<typeof ReactQuill>['onChange'] = (value, _delta, source) => {
      if (source !== 'user') {
        return;
      }
      onChange?.(value);
    };

    const handleQuillRef = useCallback((instance: ReactQuill | null) => {
      quillRef.current = instance;

      if (isNil(instance)) {
        imageClickCleanupRef.current?.();
        imageClickCleanupRef.current = null;
        return;
      }

      const editor = instance.getEditor();
      patchToolbarNullRangeUpdate(editor);

      // Selecting an image (clicking it) reveals its width controls in the footer and draws the
      // drag-resize overlay; clicking anywhere else in the editor clears the selection. The
      // listeners live on Quill's own root, so they are torn down with the editor (safe under
      // StrictMode's mount/unmount cycle).
      const handleEditorRootClick = (event: Event) => {
        // Swallow the synthetic click the browser fires at the end of a move drag.
        if (suppressClickRef.current) {
          suppressClickRef.current = false;
          return;
        }
        const target = event.target;
        const wrapper = wrapperRef.current;
        if (target instanceof HTMLImageElement && isNotNil(wrapper)) {
          selectedImageRef.current = target;
          setSelectedImage(target);
          const rect = measureImageOverlay(target, wrapper);
          overlayRectRef.current = rect;
          // On first select the overlay is not mounted yet (its ref callback positions it on
          // mount); when switching between images the node already exists, so position it now.
          if (isNotNil(overlayRef.current)) {
            applyOverlayRect(overlayRef.current, rect);
          }
        } else {
          selectedImageRef.current = null;
          setSelectedImage(null);
          overlayRectRef.current = null;
        }
      };

      // The overlay is anchored to the wrapper, not the scrolling content, so re-measure it
      // whenever the editor scrolls or the viewport resizes.
      const repositionOverlay = () => {
        const image = selectedImageRef.current;
        const wrapper = wrapperRef.current;
        const node = overlayRef.current;
        if (isNotNil(image) && isNotNil(wrapper) && isNotNil(node)) {
          const rect = measureImageOverlay(image, wrapper);
          overlayRectRef.current = rect;
          applyOverlayRect(node, rect);
        }
      };

      // Dragging an image moves it to a new position in the text flow. Native HTML5 drag events
      // don't fire reliably inside Quill's editable, so this is a pointer-based drag: press an
      // image and move past a small threshold to start; an insertion bar follows the caret under
      // the cursor; on release the embed is moved through the editor API (keeping it in the
      // value). Native image dragging is disabled in CSS so it can't interfere.
      const handleImageMovePointerMove = (event: PointerEvent) => {
        const dragState = moveDragRef.current;
        const image = dragImageRef.current;
        const wrapper = wrapperRef.current;
        if (isNil(dragState) || isNil(image) || isNil(wrapper)) {
          return;
        }
        if (!dragState.isMoving) {
          const distance = Math.hypot(
            event.clientX - dragState.startX,
            event.clientY - dragState.startY
          );
          if (distance < IMAGE_MOVE_THRESHOLD) {
            return;
          }
          dragState.isMoving = true;
          image.style.opacity = '0.4';
          editor.root.style.userSelect = 'none';
          wrapper.setAttribute('data-image-dragging', 'true');
          setIsMovingImage(true);
        }
        event.preventDefault();
        const indicatorRect = measureCaretIndicator(wrapper, event.clientX, event.clientY);
        if (isNotNil(indicatorRect)) {
          moveIndicatorRectRef.current = indicatorRect;
          if (isNotNil(moveIndicatorRef.current)) {
            applyIndicatorRect(moveIndicatorRef.current, indicatorRect);
          }
        }
      };

      const handleImageMovePointerUp = (event: PointerEvent) => {
        const dragState = moveDragRef.current;
        const image = dragImageRef.current;
        window.removeEventListener('pointermove', handleImageMovePointerMove);
        window.removeEventListener('pointerup', handleImageMovePointerUp);
        moveDragRef.current = null;
        dragImageRef.current = null;
        editor.root.style.userSelect = '';
        wrapperRef.current?.removeAttribute('data-image-dragging');
        setIsMovingImage(false);
        if (isNotNil(image)) {
          image.style.opacity = '';
        }
        // A press without crossing the threshold is a plain click (handled by the click listener).
        if (isNil(dragState) || isNil(image) || !dragState.isMoving) {
          return;
        }
        // Swallow the click the browser fires after the drag so it doesn't re-select the image.
        suppressClickRef.current = true;
        const blot = Quill.find(image);
        const dropIndex = getDropIndex(editor, event.clientX, event.clientY);
        const source = image.getAttribute('src');
        if (isNil(blot) || blot instanceof Quill || isNil(dropIndex) || isNil(source)) {
          return;
        }
        const fromIndex = editor.getIndex(blot);
        const widthAttribute = image.getAttribute('width');
        editor.deleteText(fromIndex, 1, 'user');
        // Deleting the image shifts everything after it left by one.
        const insertIndex = dropIndex > fromIndex ? dropIndex - 1 : dropIndex;
        editor.insertEmbed(insertIndex, 'image', source, 'user');
        if (isNotNil(widthAttribute)) {
          editor.formatText(insertIndex, 1, {width: widthAttribute}, 'user');
        }
        editor.setSelection(insertIndex + 1, 0, 'user');
        selectedImageRef.current = null;
        setSelectedImage(null);
        overlayRectRef.current = null;
      };

      const handleImageMovePointerDown = (event: PointerEvent) => {
        if (event.button !== 0 || !(event.target instanceof HTMLImageElement)) {
          return;
        }
        dragImageRef.current = event.target;
        moveDragRef.current = {startX: event.clientX, startY: event.clientY, isMoving: false};
        window.addEventListener('pointermove', handleImageMovePointerMove);
        window.addEventListener('pointerup', handleImageMovePointerUp);
      };

      editor.root.addEventListener('click', handleEditorRootClick);
      editor.root.addEventListener('scroll', repositionOverlay);
      editor.root.addEventListener('pointerdown', handleImageMovePointerDown);
      window.addEventListener('resize', repositionOverlay);
      // Content changes (e.g. aligning the line) can move the image without a scroll/resize, so
      // keep the overlay glued to it on every text change too.
      editor.on('text-change', repositionOverlay);
      imageClickCleanupRef.current = () => {
        editor.root.removeEventListener('click', handleEditorRootClick);
        editor.root.removeEventListener('scroll', repositionOverlay);
        editor.root.removeEventListener('pointerdown', handleImageMovePointerDown);
        window.removeEventListener('resize', repositionOverlay);
        window.removeEventListener('pointermove', handleImageMovePointerMove);
        window.removeEventListener('pointerup', handleImageMovePointerUp);
        editor.off('text-change', repositionOverlay);
      };
    }, []);

    // Quill requires referentially stable modules object — useMemo is necessary here.
    const modules = useMemo(
      () => ({
        toolbar: {
          container: `#${toolbarId}`,
        },
      }),
      [toolbarId]
    );

    const handleSelectionChange = useCallback(() => {
      const editor = quillRef.current?.getEditor();
      if (!editor) {
        return;
      }
      const range = editor.getSelection();
      if (!range) {
        return;
      }
      const formats = editor.getFormat(range);
      if (formats.header === 1) {
        setSelectionPathKey('richText.formats.heading1');
      } else if (formats.header === 2) {
        setSelectionPathKey('richText.formats.heading2');
      } else if (formats.header === 3) {
        setSelectionPathKey('richText.formats.heading3');
      } else if (formats.blockquote) {
        setSelectionPathKey('richText.formats.quote');
      } else if (formats.list === 'ordered') {
        setSelectionPathKey('richText.toolbar.numberedList');
      } else if (formats.list === 'bullet') {
        setSelectionPathKey('richText.toolbar.bulletedList');
      } else {
        setSelectionPathKey('richText.formats.paragraph');
      }
    }, []);

    const handleEmojiInsert = useCallback((emoji: string) => {
      const editor = quillRef.current?.getEditor();
      if (editor) {
        const selection = editor.getSelection();
        const index = selection ? selection.index : editor.getLength();
        editor.insertText(index, emoji, 'user');
        editor.setSelection(index + emoji.length);
      }
      setIsEmojiPickerOpen(false);
    }, []);

    // Opens a file picker and embeds the chosen image as a base64 data URL via the live editor
    // ref. This deliberately does NOT use Quill's `ql-image` toolbar handler: that handler binds
    // to our external toolbar and, under React StrictMode's double-mount, a stale copy bound to
    // the destroyed editor fires with a null selection and crashes Quill's uploader. A plain
    // React onClick is mount-safe (same approach as the emoji button).
    const handleImageInsert = useCallback(() => {
      const editor = quillRef.current?.getEditor();
      if (isNil(editor)) {
        return;
      }

      const selection = editor.getSelection();
      const insertIndex = selection ? selection.index : editor.getLength();

      const fileInput = document.createElement('input');
      fileInput.type = 'file';
      fileInput.accept = 'image/*';
      fileInput.addEventListener('change', () => {
        const file = fileInput.files?.[0];
        if (isNil(file)) {
          return;
        }

        const insertImageAtCaret = (dataUrl: string, width: number | null) => {
          editor.insertEmbed(insertIndex, 'image', dataUrl, 'user');
          if (isNotNil(width)) {
            editor.formatText(insertIndex, 1, {width: String(width), height: false}, 'user');
          }
          editor.setSelection(insertIndex + 1);
        };

        const reader = new FileReader();
        reader.addEventListener('load', () => {
          if (typeof reader.result !== 'string') {
            return;
          }
          const dataUrl = reader.result;
          // Measure the image first so an oversized upload is capped to a sensible width.
          const probe = new Image();
          probe.addEventListener('load', () => {
            // Downscale oversized uploads so the stored base64 stays small (keeps typing fast).
            const optimizedDataUrl = downscaleImageDataUrl(probe, file.type) ?? dataUrl;
            insertImageAtCaret(
              optimizedDataUrl,
              probe.naturalWidth > DEFAULT_IMAGE_WIDTH ? DEFAULT_IMAGE_WIDTH : null
            );
          });
          probe.addEventListener('error', () => insertImageAtCaret(dataUrl, null));
          probe.src = dataUrl;
        });
        reader.readAsDataURL(file);
      });
      fileInput.click();
    }, []);

    // Sets a fixed pixel width on the selected image (clearing height so the browser keeps the
    // intrinsic aspect ratio) through Quill's format API, so the change lands in the value.
    // A `null` width removes the constraint and restores the natural size.
    const handleImageResize = (width: number | null) => {
      const image = selectedImageRef.current;
      const editor = quillRef.current?.getEditor();
      if (isNil(image) || isNil(editor)) {
        return;
      }
      const blot = Quill.find(image);
      if (isNil(blot) || blot instanceof Quill) {
        selectedImageRef.current = null;
        setSelectedImage(null);
        overlayRectRef.current = null;
        return;
      }
      const index = editor.getIndex(blot);
      editor.formatText(
        index,
        1,
        {width: isNil(width) ? false : String(width), height: false},
        'user'
      );
    };

    // Live-previews the drag by setting an inline width (not tracked by Quill, so no change
    // events fire mid-drag) and keeps the overlay glued to the image.
    const handleImageResizeDragMove = (event: PointerEvent) => {
      const dragState = resizeDragRef.current;
      const image = selectedImageRef.current;
      const wrapper = wrapperRef.current;
      if (isNil(dragState) || isNil(image) || isNil(wrapper)) {
        return;
      }
      const delta = (event.clientX - dragState.startX) * dragState.directionX;
      const nextWidth = Math.max(MIN_IMAGE_WIDTH, Math.round(dragState.startWidth + delta));
      dragState.currentWidth = nextWidth;
      image.style.width = `${nextWidth}px`;
      positionOverlay(measureImageOverlay(image, wrapper));
    };

    // Commits the dragged width through the same `formatText` path the presets use, so it
    // persists in the value; the inline preview width is cleared first.
    const handleImageResizeDragEnd = () => {
      const dragState = resizeDragRef.current;
      const image = selectedImageRef.current;
      if (isNotNil(dragState) && isNotNil(image)) {
        image.style.width = '';
        // Only commit when the width actually changed, so merely clicking a handle does not
        // dirty the value.
        if (dragState.currentWidth !== dragState.startWidth) {
          handleImageResize(dragState.currentWidth);
        }
      }
      resizeDragRef.current = null;
      resizeDragCleanupRef.current?.();
      resizeDragCleanupRef.current = null;
    };

    const handleImageResizeDragStart = (
      event: ReactPointerEvent<HTMLDivElement>,
      corner: ImageResizeCorner
    ) => {
      // Prevent the mousedown from blurring the editor (which would drop the selection).
      event.preventDefault();
      event.stopPropagation();
      const image = selectedImageRef.current;
      if (isNil(image)) {
        return;
      }
      const directionX = corner === 'topLeft' || corner === 'bottomLeft' ? -1 : 1;
      const startWidth = Math.round(image.getBoundingClientRect().width);
      resizeDragRef.current = {
        startX: event.clientX,
        startWidth,
        directionX,
        currentWidth: startWidth,
      };
      window.addEventListener('pointermove', handleImageResizeDragMove);
      window.addEventListener('pointerup', handleImageResizeDragEnd);
      resizeDragCleanupRef.current = () => {
        window.removeEventListener('pointermove', handleImageResizeDragMove);
        window.removeEventListener('pointerup', handleImageResizeDragEnd);
      };
    };

    const handleEditorBlur = useCallback(() => {
      handleBlur();
      // Keep the selection/overlay alive while a resize drag is in progress.
      if (isNotNil(resizeDragRef.current)) {
        return;
      }
      selectedImageRef.current = null;
      setSelectedImage(null);
      overlayRectRef.current = null;
    }, [handleBlur]);

    const isImageSelected = enabledOptionSet.has('image') && isNotNil(selectedImage);

    return (
      <div onFocus={props.onFocus} onBlur={props.onBlur} onKeyDown={props.onKeyDown}>
        <VStack>
          <Label
            id={id}
            isRequired={props.isRequired}
            tooltip={props.tooltip}
            isRecommended={props.isRecommended}
            data-testid={suffixTestId('label', props)}
            actions={props.labelActions}
            maxLength={props.maxLength}
            isCounterVisible={props.isCounterVisible}
            currentLength={currentLength}
          >
            {props.label}
          </Label>
          <FieldWrapper
            $isDisabled={props.isDisabled}
            $isInvalid={props.isInvalid || !!props.errorMessage}
          >
            <StyledQuillWrapper
              ref={wrapperRef}
              $isFocused={isFocused}
              $isDisabled={props.isDisabled}
              $isInvalid={props.isInvalid || !!props.errorMessage}
              $isScrollable={props.isScrollable}
              $rows={props.rows}
              $minRows={props.minRows}
              $maxRows={props.maxRows}
            >
              {/* Quill requires these specific class names to bind toolbar buttons. */}
              {/* eslint-disable react/forbid-dom-props */}
              <div
                id={toolbarId}
                role="toolbar"
                aria-label={t('richText.toolbar.formattingToolbar')}
                onMouseDown={handleToolbarMouseDown}
              >
                <Show
                  when={
                    enabledOptionSet.has('bold') ||
                    enabledOptionSet.has('italic') ||
                    enabledOptionSet.has('underline') ||
                    enabledOptionSet.has('strike')
                  }
                >
                  <span className="ql-formats">
                    <Show when={enabledOptionSet.has('bold')}>
                      <button
                        className="ql-bold"
                        type="button"
                        aria-label={t('richText.toolbar.bold')}
                      />
                    </Show>
                    <Show when={enabledOptionSet.has('italic')}>
                      <button
                        className="ql-italic"
                        type="button"
                        aria-label={t('richText.toolbar.italic')}
                      />
                    </Show>
                    <Show when={enabledOptionSet.has('underline')}>
                      <button
                        className="ql-underline"
                        type="button"
                        aria-label={t('richText.toolbar.underline')}
                      />
                    </Show>
                    <Show when={enabledOptionSet.has('strike')}>
                      <button
                        className="ql-strike"
                        type="button"
                        aria-label={t('richText.toolbar.strikethrough')}
                      />
                    </Show>
                  </span>
                </Show>
                <Show when={enabledOptionSet.has('header') || enabledOptionSet.has('blockquote')}>
                  <span className="ql-formats">
                    <Show when={enabledOptionSet.has('header')}>
                      <select
                        className="ql-header"
                        defaultValue=""
                        aria-label={t('richText.toolbar.heading')}
                      >
                        <option value="1">{t('richText.formats.heading1')}</option>
                        <option value="2">{t('richText.formats.heading2')}</option>
                        <option value="3">{t('richText.formats.heading3')}</option>
                        <option value="">{t('richText.formats.paragraph')}</option>
                      </select>
                    </Show>
                    <Show when={enabledOptionSet.has('blockquote')}>
                      <button
                        className="ql-blockquote"
                        type="button"
                        aria-label={t('richText.toolbar.blockquote')}
                      />
                    </Show>
                  </span>
                </Show>
                <Show
                  when={
                    enabledOptionSet.has('listOrdered') ||
                    enabledOptionSet.has('listBullet') ||
                    enabledOptionSet.has('align')
                  }
                >
                  <span className="ql-formats">
                    <Show when={enabledOptionSet.has('listOrdered')}>
                      <button
                        className="ql-list"
                        value="ordered"
                        type="button"
                        aria-label={t('richText.toolbar.numberedList')}
                      />
                    </Show>
                    <Show when={enabledOptionSet.has('listBullet')}>
                      <button
                        className="ql-list"
                        value="bullet"
                        type="button"
                        aria-label={t('richText.toolbar.bulletedList')}
                      />
                    </Show>
                    <Show when={enabledOptionSet.has('align')}>
                      <button
                        className="ql-align"
                        value=""
                        type="button"
                        aria-label={t('richText.toolbar.alignLeft')}
                      />
                      <button
                        className="ql-align"
                        value="center"
                        type="button"
                        aria-label={t('richText.toolbar.alignCenter')}
                      />
                      <button
                        className="ql-align"
                        value="right"
                        type="button"
                        aria-label={t('richText.toolbar.alignRight')}
                      />
                      <button
                        className="ql-align"
                        value="justify"
                        type="button"
                        aria-label={t('richText.toolbar.justify')}
                      />
                    </Show>
                  </span>
                </Show>
                <Show when={enabledOptionSet.has('image')}>
                  <span className="ql-formats">
                    {/* Intentionally not `ql-image` (see handleImageInsert): a plain button with */}
                    {/* Quill's own image glyph keeps it visually identical to the native icons */}
                    {/* while the React onClick does the base64 insert, avoiding the crash. */}
                    <button
                      type="button"
                      aria-label={t('richText.toolbar.insertImage')}
                      data-testid={suffixTestId('insertImageButton', props)}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={handleImageInsert}
                    >
                      <svg viewBox="0 0 18 18">
                        <rect className="ql-stroke" height="10" width="12" x="3" y="4" />
                        <circle className="ql-fill" cx="6" cy="7" r="1" />
                        <polyline
                          className="ql-even ql-fill"
                          points="5 12 5 11 7 9 8 10 11 7 13 9 13 12 5 12"
                        />
                      </svg>
                    </button>
                  </span>
                </Show>
                <Show when={enabledOptionSet.has('clean') || enabledOptionSet.has('emoji')}>
                  <span className="ql-formats">
                    <Show when={enabledOptionSet.has('clean')}>
                      <button
                        className="ql-clean"
                        type="button"
                        aria-label={t('richText.toolbar.clearFormatting')}
                      />
                    </Show>
                    <Show when={enabledOptionSet.has('emoji')}>
                      <EmojiPickerWrapper>
                        <button
                          className="ql-emoji"
                          type="button"
                          aria-label={t('richText.toolbar.insertEmoji')}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => setIsEmojiPickerOpen((v) => !v)}
                        >
                          😀
                        </button>
                        <Show when={isEmojiPickerOpen && !props.isDisabled}>
                          <EmojiPanel data-testid={suffixTestId('emojiPicker', props)}>
                            <EmojiCategoryTabs>
                              {emojiCategories.map((category, categoryIndex) => (
                                <EmojiCategoryButton
                                  key={category.label}
                                  role="button"
                                  tabIndex={0}
                                  $isActive={categoryIndex === activeEmojiCategory}
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={() => setActiveEmojiCategory(categoryIndex)}
                                >
                                  {category.label}
                                </EmojiCategoryButton>
                              ))}
                            </EmojiCategoryTabs>
                            <EmojiGrid>
                              {emojiCategories[activeEmojiCategory].emojis.map((emoji) => (
                                <EmojiButton
                                  key={emoji}
                                  role="button"
                                  tabIndex={0}
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={() => handleEmojiInsert(emoji)}
                                >
                                  {emoji}
                                </EmojiButton>
                              ))}
                            </EmojiGrid>
                          </EmojiPanel>
                        </Show>
                      </EmojiPickerWrapper>
                    </Show>
                  </span>
                </Show>
              </div>
              {/* eslint-enable react/forbid-dom-props */}
              <ReactQuill
                ref={handleQuillRef}
                theme="snow"
                value={quillValue}
                onChange={handleChange}
                readOnly={props.isDisabled}
                placeholder={props.placeholder ?? ''}
                modules={modules}
                formats={quillFormats}
                useSemanticHTML={false}
                onFocus={handleFocus}
                onBlur={handleEditorBlur}
                onChangeSelection={handleSelectionChange}
                aria-labelledby={id}
                aria-describedby={props.errorMessage || props.helperText ? helperId : undefined}
                aria-invalid={props.isInvalid || !!props.errorMessage || undefined}
              />
              <Show when={isImageSelected && !props.isDisabled}>
                <ImageResizeOverlay
                  ref={handleOverlayRef}
                  data-testid={suffixTestId('imageResizeOverlay', props)}
                >
                  {IMAGE_RESIZE_CORNERS.map((corner) => (
                    <ImageResizeHandle
                      key={corner}
                      $corner={corner}
                      data-testid={suffixTestId(`imageResizeHandle-${corner}`, props)}
                      onPointerDown={(event) => handleImageResizeDragStart(event, corner)}
                    />
                  ))}
                </ImageResizeOverlay>
              </Show>
              <Show when={isMovingImage}>
                <ImageMoveIndicator
                  ref={handleMoveIndicatorRef}
                  data-testid={suffixTestId('imageMoveIndicator', props)}
                />
              </Show>
            </StyledQuillWrapper>
            <FooterBar>
              <Text size="xSmall" color="secondary">
                {t(selectionPathKey)}
              </Text>
              <Show when={props.isCounterVisible !== false}>
                <FooterCounter $isOverLimit={isOverLimit} $isNearLimit={isNearLimit}>
                  {buildCounterLabel(currentLength, props.maxLength)}
                </FooterCounter>
              </Show>
            </FooterBar>
          </FieldWrapper>
          <Show when={props.errorMessage || props.helperText}>
            <div id={helperId}>
              <HelperText
                errorMessage={props.errorMessage}
                helperText={props.helperText}
                data-testid={suffixTestId('helper', props)}
              />
            </div>
          </Show>
        </VStack>
      </div>
    );
  }
);
